/**
 * Tag History Page - View detailed access logs for a specific tag
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Calendar, Download, RefreshCw, 
  Monitor, Smartphone, Tablet, Globe, Clock,
  ChevronLeft, ChevronRight, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TagHistory = () => {
  const { nfcCode } = useParams();
  const navigate = useNavigate();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, total_pages: 1 });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const loadHistory = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      let url = `${API}/admin/analytics/${nfcCode}/history?page=${page}&limit=50`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;
      
      const response = await axios.get(url);
      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, total_pages: 1 });
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [nfcCode, startDate, endDate]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/analytics/${nfcCode}/export`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${nfcCode}_analytics.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Analytics exported');
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold">Access History</h1>
                <p className="text-muted-foreground mt-1">
                  Tag: <code className="bg-secondary px-2 py-0.5 rounded">{nfcCode}</code>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => loadHistory(1)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary hover:bg-secondary/80 text-sm font-medium"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium"
                >
                  <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6 flex items-center gap-4 p-4 rounded-2xl bg-card border border-border/40">
            <Filter className="w-5 h-5 text-muted-foreground" />
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">From:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-secondary rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">To:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-secondary rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={() => loadHistory(1)}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
            >
              Apply Filter
            </button>
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            )}
            <div className="ml-auto text-sm text-muted-foreground">
              {pagination.total} total scans
            </div>
          </div>

          {/* History Table */}
          <div className="rounded-2xl border border-border/40 bg-card overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No scan history found</p>
                <p className="text-sm">Scans will appear here when people view this tag</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary/50 border-b border-border/40">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Device</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Browser</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">IP Address</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {logs.map((log, index) => (
                      <tr key={index} className="hover:bg-secondary/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">
                              {new Date(log.viewed_at).toLocaleString()}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">
                              {log.city !== 'Unknown' ? `${log.city}, ` : ''}{log.country}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getDeviceIcon(log.device_type)}
                            <span className="text-sm capitalize">{log.device_type}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm">{log.browser} / {log.os}</span>
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-secondary px-2 py-0.5 rounded">{log.ip_address}</code>
                        </td>
                        <td className="px-4 py-3">
                          {log.is_unique ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">New Visitor</span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">Return</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border/40 bg-secondary/30">
                <div className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.total_pages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => loadHistory(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => loadHistory(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages}
                    className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TagHistory;
